import { green } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";

/**
 *
 * This hook provides a set of common styles that can be used throughout the application.
 * It is to ensure backward compatibility with existing components that rely on these styles.
 * We recommend using the `sx` prop for new components, but this hook is still available
 * for legacy components that may not support the `sx` prop.
 */
export const useCommonStyles = () => {
  const theme = useTheme();

  return {
    // Spacing utilities
    basicSpacing: {
      margin: theme.spacing(1),
    },
    paddingTopXSmall: {
      paddingTop: theme.spacing(0.5),
    },
    paddingTopSmall: {
      paddingTop: theme.spacing(1),
    },
    paddingBottomXSmall: {
      paddingBottom: theme.spacing(0.5),
    },
    paddingBottomSmall: {
      paddingBottom: theme.spacing(1),
    },
    padLeft: {
      paddingLeft: theme.spacing(2),
    },
    padLeftLarge: {
      paddingLeft: theme.spacing(4),
    },
    padLeftXSmall: {
      paddingLeft: theme.spacing(0.5),
    },
    spaceLeftXSmall: {
      marginLeft: theme.spacing(0.5),
    },
    spaceLeft: {
      marginLeft: theme.spacing(2),
    },
    spaceRightXSmall: {
      marginRight: theme.spacing(0.5),
    },
    spaceRight: {
      marginRight: theme.spacing(2),
    },
    spaceRightMedium: {
      marginRight: theme.spacing(3),
    },
    spaceRightLarge: {
      marginRight: theme.spacing(4),
    },
    spaceTopXSmall: {
      marginTop: theme.spacing(0.5),
    },
    spaceTopSmall: {
      marginTop: theme.spacing(1),
    },
    spaceTop: {
      marginTop: theme.spacing(2),
    },
    spaceTopLarge: {
      marginTop: theme.spacing(3),
    },
    spaceBottomSmall: {
      marginBottom: theme.spacing(1),
    },
    spaceBottom: {
      marginBottom: theme.spacing(2),
    },
    spaceBottomLarge: {
      marginBottom: theme.spacing(3),
    },
    smallSpacing: {
      margin: 2,
    },

    // Typography and appearance
    tableTypography: {
      fontSize: 14,
    },
    tableCellNew: {
      padding: 0,
    },
    appBarSpacer: theme.mixins.toolbar,
    accentGreenBackground: {
      backgroundColor: green[400],
    },

    // Links
    removeLinkStyle: {
      textDecoration: "none",
      color: "inherit",
    },
    removeVisitedLinkStyle: {
      textDecoration: "none",
      color: "inherit",
      "&:visited": {
        textDecoration: "none",
        color: "inherit",
      },
    },
    coloredLinkStyle: {
      textDecoration: "none",
      color: theme.palette.primary.main,
      "&:visited": {
        textDecoration: "none",
        color: theme.palette.primary.main,
      },
    },

    // Colors
    purpleRain500: {
      backgroundColor: "#7A7DFF",
      color: "#FFFFFF",
    },
    purpleRain: {
      backgroundColor: "#6559FF",
      color: "#FFFFFF",
    },
    blueMoon: {
      backgroundColor: "#00b1f2",
      color: "#FFFFFF",
    },
    greenDay: {
      backgroundColor: "#0AAD69",
      color: "#FFFFFF",
    },
    yellowSubmarine: {
      backgroundColor: "#FFA900",
      color: "#FFFFFF",
    },
    redWine: {
      backgroundColor: "#EA004F",
      color: "#FFFFFF",
    },
    neutral: {
      backgroundColor: "#7A869A",
      color: "#FFFFFF",
    },

    // Utilities
    hide: {
      visibility: "hidden" as const,
      display: "none",
    },
    secondaryText: {
      color: theme.palette.secondary.main,
    },
    clickable: {
      "&:hover": {
        cursor: "pointer",
      },
    },
    positionIcon: {
      position: "relative" as const,
      top: 2,
    },
    inlineIcon: {
      position: "relative" as const,
      left: 2,
      bottom: 1,
    },
    shiftRight: {
      position: "relative" as const,
      left: 10,
    },

    // Form controls
    formControl: {
      minWidth: 240,
    },
    formControlRetro: {
      minWidth: 180,
    },
    selector: {
      minWidth: 330,
      marginBottom: theme.spacing(2),
    },
    formSpacing: {
      marginBottom: theme.spacing(3),
      "& > *": {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
      },
    },
    addButton: {
      marginBottom: 3,
    },

    // Checkbox
    checkboxLabel: {
      "& .MuiFormControlLabel-label": {
        marginRight: theme.spacing(1),
      },
    },

    // Responsive utilities
    atSymbol: {
      [theme.breakpoints.down("lg")]: {
        position: "relative" as const,
        top: theme.spacing(2),
      },
    },

    // Dialog
    confirmDialogAction: {
      padding: "16px 24px",
    },

    // Toggle button
    toogleButtonColor: {
      "&.Mui-selected": {
        backgroundColor: theme.palette.primary.main,
        color: "white",
      },
    },
  };
};

// Direct style objects for common spacing patterns
export const spacing = {
  spaceSearch: (theme: any) => ({
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    "& > *": {
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(0.5),
    },
    [theme.breakpoints.down("xl")]: {
      "& > *": {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
      },
    },
  }),

  spaceDocumentTransferButtons: (theme: any) => ({
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(1),
    },
  }),
};
